/**
 * ReportDepartmentTabs Component
 *
 * Displays clickable tiles for each department on the Reports page.
 * Similar to DepartmentTabs but without the 'ALL' option.
 */

import React, { useMemo } from 'react';
import {
  TruckIcon,
  WrenchScrewdriverIcon,
  CubeIcon,
  SparklesIcon,
  KeyIcon,
  EllipsisHorizontalCircleIcon,
  MegaphoneIcon,
  BriefcaseIcon,
} from '@heroicons/react/24/outline';
import { ReportDepartmentKey } from '../../types/reports';

interface Department {
  key: ReportDepartmentKey;
  label: string;
  shortLabel: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  bgColor: string;
  borderColor: string;
}

const REPORT_DEPARTMENTS: Department[] = [
  {
    key: 'NEW_VEHICLE',
    label: 'New Vehicles',
    shortLabel: 'New',
    icon: TruckIcon,
    color: 'text-blue-700',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-300',
  },
  {
    key: 'USED_VEHICLE',
    label: 'Used Vehicles',
    shortLabel: 'Used',
    icon: TruckIcon,
    color: 'text-green-700',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-300',
  },
  {
    key: 'SERVICE',
    label: 'Service',
    shortLabel: 'Service',
    icon: WrenchScrewdriverIcon,
    color: 'text-orange-700',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-300',
  },
  {
    key: 'PARTS',
    label: 'Parts',
    shortLabel: 'Parts',
    icon: CubeIcon,
    color: 'text-purple-700',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-300',
  },
  {
    key: 'BODY_SHOP',
    label: 'Body Shop',
    shortLabel: 'Body',
    icon: SparklesIcon,
    color: 'text-red-700',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-300',
  },
  {
    key: 'RENTAL',
    label: 'Rental',
    shortLabel: 'Rental',
    icon: KeyIcon,
    color: 'text-teal-700',
    bgColor: 'bg-teal-50',
    borderColor: 'border-teal-300',
  },
  {
    key: 'OTHERS',
    label: 'Others',
    shortLabel: 'Others',
    icon: EllipsisHorizontalCircleIcon,
    color: 'text-slate-700',
    bgColor: 'bg-slate-50',
    borderColor: 'border-slate-300',
  },
  {
    key: 'MARKETING',
    label: 'Marketing',
    shortLabel: 'Marketing',
    icon: MegaphoneIcon,
    color: 'text-pink-700',
    bgColor: 'bg-pink-50',
    borderColor: 'border-pink-300',
  },
  {
    key: 'EXECUTIVE',
    label: 'Executive',
    shortLabel: 'Executive',
    icon: BriefcaseIcon,
    color: 'text-amber-700',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-300',
  },
];

const EXECUTIVE_ROLES = ['Admin', 'CEO', 'Executive', 'GeneralManager'];

interface ReportDepartmentTabsProps {
  selectedDepartment: ReportDepartmentKey;
  onDepartmentChange: (department: ReportDepartmentKey) => void;
  assignedDepartmentIds?: string[];
  userRole?: string | null;
}

export const ReportDepartmentTabs: React.FC<ReportDepartmentTabsProps> = ({
  selectedDepartment,
  onDepartmentChange,
  assignedDepartmentIds,
  userRole,
}) => {
  const visibleDepartments = useMemo(() => {
    let depts = REPORT_DEPARTMENTS;
    if (assignedDepartmentIds && assignedDepartmentIds.length > 0) {
      depts = depts.filter(d => {
        // Marketing tab is also visible to anyone with NEW_VEHICLE or
        // USED_VEHICLE access — sales-floor DepartmentManagers legitimately
        // need the lead-pacing / ad-expense reports even when MARKETING
        // isn't explicitly in their assigned-departments list.
        if (d.key === 'MARKETING') {
          return assignedDepartmentIds.includes('MARKETING')
            || assignedDepartmentIds.includes('NEW_VEHICLE')
            || assignedDepartmentIds.includes('USED_VEHICLE');
        }
        return assignedDepartmentIds.includes(d.key);
      });
    }
    // Hide Executive tab unless user is Admin/CEO/Executive/GeneralManager
    if (!userRole || !EXECUTIVE_ROLES.includes(userRole)) {
      depts = depts.filter(d => d.key !== 'EXECUTIVE');
    }
    return depts;
  }, [assignedDepartmentIds, userRole]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
      <h3 className="text-sm font-medium text-gray-700 mb-3">Select Department</h3>
      <div className="flex flex-wrap gap-2">
        {visibleDepartments.map((dept) => {
          const isSelected = selectedDepartment === dept.key;
          const Icon = dept.icon;

          return (
            <button
              key={dept.key}
              onClick={() => onDepartmentChange(dept.key)}
              className={`
                flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all min-w-[72px] flex-1
                ${isSelected
                  ? `${dept.bgColor} ${dept.borderColor} ${dept.color} shadow-sm`
                  : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50 hover:border-gray-300'
                }
              `}
            >
              <Icon className={`h-6 w-6 mb-1 ${isSelected ? dept.color : 'text-gray-400'}`} />
              <span className={`text-xs font-medium ${isSelected ? dept.color : 'text-gray-600'}`}>
                {dept.shortLabel}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Get department display info
 */
export function getReportDepartmentLabel(key: ReportDepartmentKey): string {
  return REPORT_DEPARTMENTS.find((d) => d.key === key)?.label || key;
}
